import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchTournamentsListAPI,
  fetchTournamentCommentsAPI,
  deleteCommentDirectAPI,
  deleteAllUserCommentsAPI,
  banUserFromCommentingAPI,
} from "./tournamentAllCommentsAPI";
import { showModal } from "../statusModal/statusModal";

const initialState = {
  tournaments: [],
  tournamentsPage: 1,
  tournamentsHasMore: true,
  tournamentsStatus: "idle",
  comments: [],
  selectedTournamentId: null,
  searchQuery: "",
  currentPage: 1,
  hasMore: true,
  fetchStatus: "idle",
  actionStatus: "idle",
  error: null,
};

export const fetchTournamentsList = createAsyncThunk(
  "tournamentAllComments/fetchTournamentsList",
  async ({ page = 1, limit = 10 } = {}, { dispatch, rejectWithValue }) => {
    try {
      const data = await fetchTournamentsListAPI({ page, limit });
      return data;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء جلب البطولات",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch tournaments",
      );
    }
  },
);

export const fetchTournamentComments = createAsyncThunk(
  "tournamentAllComments/fetchTournamentComments",
  async (
    { tournamentId, search, page = 1, limit = 20 },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const data = await fetchTournamentCommentsAPI({
        tournamentId,
        search,
        page,
        limit,
      });
      return data;
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء جلب التعليقات",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch comments",
      );
    }
  },
);

export const deleteCommentDirect = createAsyncThunk(
  "tournamentAllComments/deleteCommentDirect",
  async (commentId, { dispatch, rejectWithValue }) => {
    try {
      const data = await deleteCommentDirectAPI(commentId);
      dispatch(
        showModal({
          message: "تم حذف التعليق بنجاح",
          status: "success",
        }),
      );
      return { commentId, data };
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء حذف التعليق",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete comment",
      );
    }
  },
);

export const deleteAllUserComments = createAsyncThunk(
  "tournamentAllComments/deleteAllUserComments",
  async ({ userId, tournamentId }, { dispatch, rejectWithValue }) => {
    try {
      const data = await deleteAllUserCommentsAPI(userId, tournamentId);
      dispatch(
        showModal({
          message: "تم حذف جميع تعليقات المستخدم في هذه البطولة بنجاح",
          status: "success",
        }),
      );
      return { userId, tournamentId, data };
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء حذف تعليقات المستخدم",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user comments",
      );
    }
  },
);

export const banUserFromCommenting = createAsyncThunk(
  "tournamentAllComments/banUserFromCommenting",
  async ({ userId, tournamentId }, { dispatch, rejectWithValue }) => {
    try {
      const data = await banUserFromCommentingAPI(userId, tournamentId);
      dispatch(
        showModal({
          message: "تم حظر المستخدم من التعليق في هذه البطولة بنجاح",
          status: "success",
        }),
      );
      return { userId, tournamentId, data };
    } catch (error) {
      dispatch(
        showModal({
          message: "حصل خطأ اثناء حظر المستخدم",
          status: "error",
        }),
      );
      return rejectWithValue(
        error.response?.data?.message || "Failed to ban user",
      );
    }
  },
);

const tournamentAllCommentsSlice = createSlice({
  name: "tournamentAllComments",
  initialState,
  reducers: {
    setSelectedTournament: (state, action) => {
      state.selectedTournamentId = action.payload;
      state.comments = [];
      state.currentPage = 1;
      state.hasMore = true;
      state.searchQuery = "";
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      state.comments = [];
      state.currentPage = 1;
      state.hasMore = true;
    },
    resetAllCommentsState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tournaments list
      .addCase(fetchTournamentsList.pending, (state) => {
        state.tournamentsStatus = "loading";
      })
      .addCase(fetchTournamentsList.fulfilled, (state, action) => {
        state.tournamentsStatus = "succeeded";
        const { items, page, limit } = action.payload;

        if (page === 1) {
          state.tournaments = items;
        } else {
          state.tournaments = [...state.tournaments, ...items];
        }

        state.tournamentsPage = page;
        state.tournamentsHasMore = items.length === limit;
      })
      .addCase(fetchTournamentsList.rejected, (state) => {
        state.tournamentsStatus = "failed";
      })
      // Fetch tournament comments
      .addCase(fetchTournamentComments.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchTournamentComments.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded";
        const { items, page, limit } = action.payload;

        if (page === 1) {
          state.comments = items;
        } else {
          state.comments = [...state.comments, ...items];
        }

        state.currentPage = page;
        state.hasMore = items.length === limit;
      })
      .addCase(fetchTournamentComments.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload;
      })
      // Delete comment direct
      .addCase(deleteCommentDirect.pending, (state) => {
        state.actionStatus = "loading";
      })
      .addCase(deleteCommentDirect.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        const { commentId } = action.payload;
        state.comments = state.comments.filter(
          (c) => String(c._id) !== String(commentId),
        );
      })
      .addCase(deleteCommentDirect.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.error = action.payload;
      })
      // Delete all user comments
      .addCase(deleteAllUserComments.pending, (state) => {
        state.actionStatus = "loading";
      })
      .addCase(deleteAllUserComments.fulfilled, (state, action) => {
        state.actionStatus = "succeeded";
        const { userId } = action.payload;
        state.comments = state.comments.filter(
          (c) => String(c.userId || c.user?._id) !== String(userId),
        );
      })
      .addCase(deleteAllUserComments.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.error = action.payload;
      })
      // Ban user from commenting
      .addCase(banUserFromCommenting.pending, (state) => {
        state.actionStatus = "loading";
      })
      .addCase(banUserFromCommenting.fulfilled, (state) => {
        state.actionStatus = "succeeded";
      })
      .addCase(banUserFromCommenting.rejected, (state, action) => {
        state.actionStatus = "failed";
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedTournament,
  setSearchQuery,
  resetAllCommentsState,
} = tournamentAllCommentsSlice.actions;

export default tournamentAllCommentsSlice.reducer;
